import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSequenceTemplates } from "@/hooks/use-shared-queries";
import type { PulseSequence } from "@shared/schema";
import { logger } from "@/services/debug-logger";
import { compareSequenceIds } from "@/lib/sequence-utils";

interface PulseSequenceIndicatorsProps {
  pulseId: number;
  projectId: string;
  className?: string;
  sequences?: any[]; // Pre-loaded sequences from batch metadata
}

export function PulseSequenceIndicators({ pulseId, projectId, className = "", sequences }: PulseSequenceIndicatorsProps) {
  // Use pre-loaded sequences if available, otherwise fetch individually
  // IMPORTANT: Empty array from batch data (sequences = []) is valid and prevents individual fetch
  const shouldFetchIndividual = Boolean(pulseId) && sequences === undefined;
  const { data: individualSequences = [] } = useQuery<PulseSequence[]>({
    queryKey: ["/api/pulses", pulseId, "sequences"],
    enabled: shouldFetchIndividual, // Only fetch if no batch data
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000
  });
  
  // Only warn if individual query is unexpectedly enabled (indicates missing batch data)
  useEffect(() => {
    if (shouldFetchIndividual) {
      logger.warn('PULSE_SEQUENCES', `Individual sequence query ENABLED for pulse ${pulseId} - batch data missing`);
    }
  }, [pulseId, shouldFetchIndividual]);
  
  // Use batch-loaded sequences if available, ensuring we prioritize batch data
  const pulseSequences = sequences !== undefined ? sequences : individualSequences;

  // Use shared query hook for sequence templates
  const { data: sequenceTemplates = [] } = useSequenceTemplates();

  // Load sequence colors from templates - use useMemo to prevent re-renders
  const sequenceColors = useMemo(() => {
    const colors: Record<string, string> = {};
    
    // Load colors from sequence templates for consistency
    for (const pulseSeq of pulseSequences) {
      const template = sequenceTemplates.find(t => t.sequenceNumber === pulseSeq.sequenceNumber);
      if (template) {
        colors[pulseSeq.sequenceNumber] = template.defaultColor;
      } else {
        colors[pulseSeq.sequenceNumber] = '#3B82F6'; // Default blue
      }
    }
    
    return colors;
  }, [pulseSequences, sequenceTemplates]);

  if (pulseSequences.length === 0) return null;

  // Sort sequences: MST first, then numeric (01-99), then alphabetic (A-Z)
  const sortedSequences = [...pulseSequences].sort((a, b) => compareSequenceIds(a.sequenceNumber, b.sequenceNumber));

  // Show only first 3 sequences and add "+" if there are more
  const displaySequences = sortedSequences.slice(0, 3);
  const hasMoreSequences = sortedSequences.length > 3;

  return (
    <div className={`flex gap-0.5 items-center ${className}`}>
      {displaySequences.map((pulseSeq) => {
        const color = sequenceColors[pulseSeq.sequenceNumber] || '#3B82F6';
        
        return (
          <span
            key={pulseSeq.sequenceNumber}
            className="inline-block text-xs font-medium rounded px-0.5 py-0 text-white"
            style={{ backgroundColor: color, minWidth: '14px', textAlign: 'center', fontSize: '10px', lineHeight: '14px' }}
            title={`Sequence ${pulseSeq.sequenceNumber}`}
          >
            {pulseSeq.sequenceNumber}
          </span>
        );
      })}
      {hasMoreSequences && (
        <span
          className="inline-block text-xs font-medium rounded px-0.5 py-0 text-gray-400 bg-gray-600"
          style={{ minWidth: '14px', textAlign: 'center', fontSize: '10px', lineHeight: '14px' }}
          title={`+${sortedSequences.length - 3} more sequences`}
        >
          +{sortedSequences.length - 3}
        </span>
      )}
    </div>
  );
}